import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import login_image from "../../assets/images/Auth/login.png";
import logo from "../../assets/images/Auth/tt-logo.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { loginUser, googleLoginUser, API } from "../../apis/apiService.js"; // Import your API service method
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setloginUserName } from "../../selectSlice.js";
import { GoogleLogin } from "@react-oauth/google";

function Login({ setIsAuthenticated ,setTwoFactorToken }) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const dispatch = useDispatch();
  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const getSsoCallbackUrl = (targetApp) => {
    const target = (targetApp || "").toLowerCase();
    const isStaging = window.location.hostname.includes("bacet.org") || window.location.hostname.includes("dev");

    if (target === "lms") return process.env.REACT_APP_LMS_CALLBACK_URL;
    if (target === "deals") {
      if (isStaging) {
        return "https://dev2.technotackle.in/deals/web/site/sso-callback";
      }
      return process.env.REACT_APP_DEALS_CALLBACK_URL || "https://deals.technotackle.in/web/site/sso-callback";
    }
    if (target === "crm") return process.env.REACT_APP_CRM_CALLBACK_URL;
    if (target === "hrms") {
      if (isStaging) {
        return "https://devhrms.technotackle.in/web/site/sso-callback";
      }
      return process.env.REACT_APP_HRMS_CALLBACK_URL || "https://hrms.technotackle.in/web/site/sso-callback";
    }
    return null;
  };

  const getSsoAppKey = (targetApp) => {
    const target = (targetApp || "").toLowerCase();
    if (target === "crm") return "crm";
    if (target === "deals") return "deals";
    if (target === "lms") return "lms";
    if (target === "hrms") return "hrms";
    return null;
  };

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const searchParams = new URLSearchParams(window.location.search);
    const ssoTarget = searchParams.get("sso_target");
    const callbackUrl = getSsoCallbackUrl(ssoTarget);

    if (token && callbackUrl) {
      setLoading(true);
      const appKey = getSsoAppKey(ssoTarget);
      API.post("sso/issue-ticket", { target_app: appKey })
        .then((res) => {
          const data = res.data;
          if (data.status && data.ticket) {
            window.location.href = `${callbackUrl}?ticket=${data.ticket}`;
          } else {
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error("SSO Auto Ticket Error:", err);
          setLoading(false);
        });
      return;
    }

    const checkSession = () => {
      // H6: In-flight guard — prevent concurrent /sso/session-user calls on rapid focus events
      if (checkSession._inFlight) return;
      checkSession._inFlight = true;

      // Check if pms_session cookie exists (silent SSO via cookie)
      API.get("sso/session-user")
        .then((res) => {
          if (res.data && res.data.status && (res.data.token || res.data.access_token)) {
            const user = res.data.user;
            const authToken = res.data.token || res.data.access_token;

            sessionStorage.removeItem("masterOptionsFetched");
            localStorage.setItem("access_token", "secure-cookie-auth");

            dispatch(setloginUserName(user));
            if (setIsAuthenticated) setIsAuthenticated(true);

            const callbackUrl = getSsoCallbackUrl(ssoTarget);
            if (callbackUrl) {
              const appKey = getSsoAppKey(ssoTarget);
              API.post("sso/issue-ticket", { target_app: appKey })
                .then((tRes) => {
                  const tData = tRes.data;
                  if (tData.status && tData.ticket) {
                    window.location.href = `${callbackUrl}?ticket=${tData.ticket}`;
                  } else {
                    navigate("/dashboard");
                  }
                })
                .catch(() => { navigate("/dashboard"); });
              return;
            }

            navigate("/dashboard");
          }
        })
        .catch(() => {
          // No active session — clear any stale access_token to prevent redirect loops
          localStorage.removeItem("access_token");
        })
        .finally(() => {
          checkSession._inFlight = false;
        });
    };
    checkSession._inFlight = false;

    // 1. Check immediately on mount (single check for existing SSO session)
    checkSession();

    // 2. Re-check when user switches back to this tab (e.g. after logging in via Deals CRM in another tab)
    const handleVisibility = () => {
      if (!document.hidden && document.visibilityState === "visible") {
        checkSession();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [navigate, dispatch, setIsAuthenticated]);



  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse.credential) return;
    setLoading(true);
    try {
      const response = await googleLoginUser(credentialResponse.credential);
      if (response.status && (response.access_token || response.token)) {
        const { user } = response;
        const token = response.access_token || response.token;

        sessionStorage.removeItem("masterOptionsFetched");
        localStorage.setItem("access_token", "secure-cookie-auth");
        
        dispatch(setloginUserName(user));
        toast.success("Google SSO Login Successful!");
        setIsAuthenticated(true);

        const searchParams = new URLSearchParams(window.location.search);
        const ssoTarget = searchParams.get("sso_target");
        const callbackUrl = getSsoCallbackUrl(ssoTarget);
        if (callbackUrl) {
          try {
            const appKey = getSsoAppKey(ssoTarget);
            const ticketRes = await API.post("sso/issue-ticket", { target_app: appKey });
            const ticketData = ticketRes.data;
            if (ticketData.status && ticketData.ticket) {
              window.location.href = `${callbackUrl}?ticket=${ticketData.ticket}`;
              return;
            }
          } catch (ticketErr) {
            console.error("SSO Ticket Issue Error:", ticketErr);
          }
        }

        navigate("/dashboard");
      } else {
        toast.error(response.message || "Google Login Failed.");
      }
    } catch (err) {
      toast.error(err.message || "Google Login Error.");
    } finally {
      setLoading(false);
    }
  };

  // const onSubmit = async (data) => {
  //   setLoading(true); // Start loading
  //   try {
  //     const response = await loginUser(data.email, data.password);
  //     const {access_token} = response;
  //     localStorage.setItem('access_token',access_token); // Save token

  //     toast.success('Login Successful!!');
  //     navigate('/'); // Navigate to dashboard
  //   } catch (error) {
  //     if (error.message) {
  //       setError('api', { message: error.message });
  //     }
  //     toast.error('Invalid login credentials.');
  //   } finally {
  //     setLoading(false); // Stop loading
  //   }
  // };
  const onSubmit = async (data) => {
    setLoading(true); // Start loading
    try {
      const response = await loginUser(data.email, data.password);

      // Check API response for status
      if (response.status === false) {
        throw new Error(response.message); // Throw an error to handle in catch
      }

      // Success case
      const { token, user, require_2fa, step,message } = response;
      // const { name, id, role, is_2fa_verified } = response.user;
      // localStorage.setItem("login_user_name", name);
      // // //console.log()("login_user_details :", user);
      // localStorage.setItem("login_user_details", JSON.stringify(user));
      // localStorage.setItem("login_user_id", id); // Save user id
      // localStorage.setItem("login_user_role", role);
      // localStorage.setItem("access_token", token); // Save token
      // dispatch(setloginUserName(user));
      // toast.success("Logged in Successfully!");
      // console.log(
      //   "is_2fa_verified type:",
      //   typeof is_2fa_verified,
      //   is_2fa_verified
      // );
      // console.log("response",response);
      
      // console.log("step",require_2fa);
      
      if (step === "dashboard" || message ==="Login successful" ) {
        
        if (user) {
          localStorage.setItem("access_token", "secure-cookie-auth"); // Store dummy token for frontend auth state
          dispatch(setloginUserName(user));
        }

        toast.success("Logged in Successfully!");
        setIsAuthenticated(true);
        window.location.href = "/dashboard";
      }
       else {
        //  console.log("step up and inside",token);
        setIsAuthenticated(false);
        if (step === "setup_2fa") {
           setTwoFactorToken(token);

           
          // localStorage.setItem("access_token", token); // Save token
          navigate("/twofactor-scan");
        } else if (step === "verify_otp") {
          // console.log("inside the verify_otp",token);
          
          // localStorage.setItem("access_token", token); // Save token
 setTwoFactorToken(token);
          navigate("/twofactor-auth");
        } else {
          toast.error("Invalid 2FA step. Please contact support.");
        }
      }

    } catch (error) {
      // Show error message from API or a generic error
        // console.log("error from login",error);
        // console.log(("error response",error.status));
        
        
        if (error.status ) {
      const step= error.step;
      const token =error.token;
      // const { step, token } = data;
      // console.log("step",step,token);
      

      if (step === "verify_otp") {
        setTwoFactorToken(token);
        navigate("/twofactor-auth");
        return;
      }

      if (step === "setup_2fa") {
        setTwoFactorToken(token);
        navigate("/twofactor-scan");
        return;
      }
    }
      
      const errorMessage = error.message || "Invalid login credentials.";
      toast.error(errorMessage);
      let reset_token;
      if (error.reset_token) {
        reset_token = error.reset_token;
      }
      if (error.redirect_url) {
        // console.log("redirect_url", error.redirect_url);
        setTimeout(() => {
          navigate(`/reset-password/${reset_token}`);
          // window.location.href = error.redirect_url;
        }, 2000);
      }
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        {/* Image Section */}
        <div className="col-md-7 text-center p-5 login_bg">
          <img src={login_image} alt="Illustration" className="Authleft_img" />
        </div>
        {/* Form Section */}
        <div className="col-md-5 p-5">
          <div className="text-center">
            <img src={logo} alt="logo" />
          </div>
          <div className="my-4 text-center">
            <h3>Login to your Account</h3>
          </div>

          {/* Google SSO Login for Staff */}
          <div className="mb-4 d-flex flex-column align-items-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => toast.error("Google Login Failed.")}
              shape="pill"
              theme="outline"
              size="large"
              width="100%"
              text="signin_with"
            />
            <div className="w-100 text-center my-3 position-relative">
              <hr />
              <span 
                className="position-absolute top-50 start-50 translate-middle px-3 text-muted bg-white"
                style={{ fontSize: "13px", fontWeight: 500 }}
              >
                OR LOGIN WITH EMAIL
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="login_form">
            {/* Email Field */}
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email *
              </label>
              <input
                type="email"
                id="email"
                className="form-control"
                placeholder="example@technotackle.com"
                {...register("email", { required: "Email is required" })}
              />
              {errors.email && (
                <small className="text-danger">{errors.email.message}</small>
              )}
            </div>
            {/* Password Field */}
            <div className="mb-3 position-relative">
              <label htmlFor="password" className="form-label">
                Password *
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="form-control"
                {...register("password", { required: "Password is required" })}
              />
              <span
                onClick={togglePasswordVisibility}
                style={{
                  position: "absolute",
                  top: "55px",
                  right: "10px",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
              {errors.password && (
                <small className="text-danger">{errors.password.message}</small>
              )}
            </div>
            {/* Forgot Password */}
            <div className="mb-3 text-end">
              <a
                href="#"
                className="text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/forgot_password");
                }}
              >
                Forgot Password?
              </a>
            </div>
            {/* Submit Button */}
            <div className="d-grid">
              <button type="submit" className="auth_btn" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
